import { useCallback, useEffect, useState } from 'react';
import { cragsService } from '../../services/crags';
import type { Crag } from '../../types/crag';
import { useGymAdmin } from '../context/gym-admin-context';

/**
 * Loads the full Crag record for the crag currently selected in the sidebar —
 * the crag counterpart of `useCurrentGym`. Yields nothing while a gym is
 * selected, so a page can hold both hooks and render whichever answered.
 */
export function useCurrentCrag() {
  const { selectedGymSlug, locationType } = useGymAdmin();
  const [crag, setCrag] = useState<Crag | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      setCrag(await cragsService.getCragBySlug(slug));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load crag');
      setCrag(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedGymSlug || locationType !== 'crag') {
      setCrag(null);
      setLoading(false);
      return;
    }
    load(selectedGymSlug);
  }, [selectedGymSlug, locationType, load]);

  const reload = useCallback(() => {
    if (selectedGymSlug && locationType === 'crag') load(selectedGymSlug);
  }, [selectedGymSlug, locationType, load]);

  return { crag, loading, error, setCrag, reload, slug: selectedGymSlug };
}
